import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, FileText, PenLine } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { openDdt } from "@/components/warehouse/MovementsTab";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const SignaturePad = ({ onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const ink = useRef(0);
  const last = useRef(null);
  useEffect(() => {
    const c = ref.current;
    const r = c.getBoundingClientRect();
    c.width = r.width * 2;
    c.height = r.height * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0b1f3a";
  }, []);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
  const down = (e) => { e.preventDefault(); ref.current.setPointerCapture(e.pointerId); drawing.current = true; last.current = pos(e); const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    ink.current += Math.hypot(p[0] - last.current[0], p[1] - last.current[1]);
    last.current = p;
    const ctx = ref.current.getContext("2d"); ctx.lineTo(...p); ctx.stroke();
  };
  const up = () => { if (drawing.current) { drawing.current = false; onChange(ink.current > 80 ? ref.current.toDataURL("image/png") : ""); } };
  const clear = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); ink.current = 0; onChange(""); };
  return (
    <div>
      <canvas ref={ref} className="h-44 w-full touch-none border-2 border-dashed border-zinc-400 bg-white" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} data-testid="tech-ddt-signature-pad" />
      <div className="mt-1 flex justify-between text-xs text-muted-foreground"><span>Firma con il dito nel riquadro</span><button type="button" className="inline-flex items-center gap-1 underline" onClick={clear} data-testid="tech-ddt-signature-clear"><Eraser className="h-3 w-3" />Cancella</button></div>
    </div>
  );
};

const SignSheet = ({ ddt, onClose, onSigned }) => {
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const sign = async () => {
    setBusy(true);
    try {
      await api.post(`/tech/ddt/${ddt.id}/sign`, { signature: sig });
      toast.success(`DDT ${ddt.number} firmato`);
      onSigned();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={!!ddt} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto" data-testid="tech-ddt-sign-sheet">
        <SheetHeader><SheetTitle>Firma DDT n. {ddt?.number}</SheetTitle></SheetHeader>
        {ddt && (
          <div className="mt-4 space-y-4">
            <div className="border p-3 text-sm">
              <div className="micro-label mb-1">Ritiro da {ddt.from_name}</div>
              {ddt.lines.map((l) => <div key={l.code} className="flex justify-between gap-2 border-b py-1 last:border-0"><span><span className="font-mono text-xs">{l.code}</span> · {l.name}{l.serials?.length > 0 && <span className="block font-mono text-[11px] text-muted-foreground">S/N {l.serials.join(", ")}</span>}</span><b className="font-mono">{l.qty} {l.unit}</b></div>)}
            </div>
            <Button variant="outline" className="w-full" onClick={() => openDdt(ddt.id, "/tech/ddt")} data-testid="tech-ddt-view"><FileText className="mr-2 h-4 w-4" />Vedi il DDT completo</Button>
            <SignaturePad onChange={setSig} />
            <Button className="h-12 w-full text-base" disabled={!sig || busy} onClick={sign} data-testid="tech-ddt-sign-confirm"><PenLine className="mr-2 h-5 w-5" />Firma e conferma il ritiro</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const TechDdt = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/tech/ddt").then((r) => setList(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!list.length) return null;
  const todo = list.filter((d) => !d.signed_at);
  return (
    <div className="mb-4 border bg-white p-3" data-testid="tech-ddt-list">
      <div className="micro-label mb-2">Documenti di trasporto {todo.length > 0 && <span className="ml-1 bg-amber-100 px-1.5 text-amber-800">{todo.length} da firmare</span>}</div>
      {[...todo, ...list.filter((d) => d.signed_at).slice(0, 5)].map((d) => (
        <div key={d.id} className="flex items-center justify-between gap-2 border-b py-2 text-sm last:border-0" data-testid={`tech-ddt-${d.id}`}>
          <span><b className="font-mono">DDT {d.number}</b><span className="block text-xs text-muted-foreground">{d.lines.length} {d.lines.length === 1 ? "articolo" : "articoli"} · {d.from_name}</span></span>
          {d.signed_at
            ? <button type="button" className="text-xs font-semibold text-emerald-700 underline" onClick={() => openDdt(d.id, "/tech/ddt")} data-testid={`tech-ddt-open-${d.id}`}>Firmato · apri</button>
            : <Button size="sm" onClick={() => setOpen(d)} data-testid={`tech-ddt-sign-${d.id}`}><PenLine className="mr-1 h-4 w-4" />Firma</Button>}
        </div>
      ))}
      <SignSheet ddt={open} onClose={() => setOpen(null)} onSigned={() => { setOpen(null); load(); }} />
    </div>
  );
};
